/* eslint-disable react/prop-types */
import { get } from 'lodash';
import {
  CrmType,
  Language,
  Condition,
  FinanceType,
  ExtendedFinanceType,
  FeeCategory,
  InsuranceRequestType,
} from '@upstart/harmony/lib/constants';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import {
  ToggleButton,
  DisabledToggleButton,
  ReverseToggleButton,
  TranslationRulesWithPreviewLink,
} from './components';

import { getStep4UiSchema } from './step4UiSchema';

export const step4UiSchema = getStep4UiSchema(ToggleButton);

export const newDealerUiSchema = {
  isIndependent: {
    classNames: 'no-padding-left type-ahead-container no-margin-checkbox',
  },
  brand: {
    'ui:field': 'MultiSchemaField',
    classNames: 'no-padding-left type-ahead-container',
  },
};

export const step1UiSchema = {
  'ui:order': ['*', 'privacyPolicyUrl'],
  id: {
    'ui:readonly': true,
  },
  name: {
    classNames: 'col-md-3 no-padding-left',
  },
  dealergroup_id: {
    classNames: 'col-md-3 no-padding-left',
  },
  logoFile: {
    classNames: 'col-md-3 no-padding-left',
  },
  stickerLogoFile: {
    'ui:help': '900px Wide / 175px Max Height (Recommended: 900x175)',
    classNames: 'col-md-3 no-padding-left',
  },
  isIndependent: {
    classNames: 'col-md-3 no-padding-left no-padding-bottom no-margin-checkbox',
  },
  brand: {
    'ui:field': 'MultiSchemaField',
    classNames: 'col-md-12 no-padding-left type-ahead-container',
  },
  dealerType: {
    classNames: 'col-md-12 no-padding-left',
  },
  // countryToggle is the visible option to switch between countries
  countryToggle: {
    classNames: 'col-md-3 no-padding-left clear-both',
  },
  // country is the actual value to be passed to backend
  country: {
    'ui:widget': 'hidden',
  },
  address: {
    classNames: 'col-md-10 no-padding-left',
  },
  city: {
    classNames: 'col-md-3 no-padding-left',
  },
  county: {
    classNames: 'col-md-3 no-padding-left',
  },
  province: {
    classNames: 'col-md-2 no-padding-left',
  },
  state: {
    classNames: 'col-md-2 no-padding-left',
  },
  zip: {
    classNames: 'col-md-2 no-padding-left',
  },
  phone: {
    classNames: 'col-md-2 no-padding-left',
  },
  signedOn: {
    'ui:help': 'When this dealer signed on with Upstart',
    classNames: 'col-md-4 no-padding-left',
  },
  liveAt: {
    'ui:help': 'When this dealer went live in production',
    classNames: 'col-md-4 no-padding-left',
  },
  cancelledAt: {
    'ui:help': 'When this dealer cancelled with Upstart',
    classNames: 'col-md-4 no-padding-left',
  },
  dsm: {
    classNames: 'col-md-4 no-padding-left',
  },
  contactEmail: {
    classNames: 'col-md-4 no-padding-left',
  },
  financeEmail: {
    classNames: 'col-md-4 no-padding-left',
  },
  privacyPolicyUrl: {
    classNames: 'col-md-6 no-padding-left',
  },
};

export const step2UiSchema = {
  valueGuarantee: {
    reviews: {
      classNames: 'nested-form-group',
      items: {
        logo: {
          classNames: 'col-md-4 no-padding-left',
        },
        rating: {
          classNames: 'col-md-4 no-padding-left',
        },
        reviews: {
          classNames: 'col-md-4 no-padding-left',
        },
      },
    },
    description: {
      classNames: 'dealer-value-guarantee-description nested-form-group',
      items: {
        'ui:widget': 'textarea',
      },
    },
  },
};

export const step3UiSchema = {
  feeds: {
    'ui:widget': 'checkboxes',
    classNames: 'col-md-6 nested-form-group',
  },
  inStoreFeedview_id: {
    classNames: 'form-group field field-string col-md-6',
  },
  onlineFeedview_id: {
    classNames: 'form-group field field-string col-md-6',
  },
  canImportInventory: {
    classNames: 'form-group field field-boolean col-md-6',
  },
};

export const getStep5UiSchema = formData => {
  const eContractingEnabled = formData?.featureConfig?.eContracting?.enabled;
  return {
    managers: {
      'ui:options': {
        orderable: false,
      },
      items: {
        name: {
          classNames: 'col-md-4',
          'ui:emptyValue': '',
        },
        email: {
          classNames: 'col-md-4',
          'ui:emptyValue': '',
        },
        phone: {
          classNames: 'col-md-4',
          'ui:emptyValue': '',
        },
        shouldSendTurnover: {
          classNames: 'col-md-2',
        },
        shouldSendLead: {
          classNames: 'col-md-2',
        },
        shouldSendRemoteTestDrive: {
          classNames: 'col-md-2',
        },
        shouldSendPickup: {
          classNames: 'col-md-2',
        },
        shouldSendDelivery: {
          classNames: 'col-md-2',
        },
        shouldSendTestDrive: {
          classNames: 'col-md-2',
        },
        shouldSendCreditApplication: {
          classNames: 'col-md-2',
        },
        shouldReceiveSignatures: {
          classNames: 'col-md-3',
          'ui:widget': eContractingEnabled ? undefined : 'hidden',
        },
        isPrimaryNewSigner: {
          classNames: 'col-md-3',
          'ui:widget': eContractingEnabled ? undefined : 'hidden',
        },
        isPrimaryUsedSigner: {
          classNames: 'col-md-3',
          'ui:widget': eContractingEnabled ? undefined : 'hidden',
        },
      },
    },
    disabledNotifications: {
      emails: {
        testDrive: {
          'ui:widget': ReverseToggleButton,
        },
        testDriveIsWaiting: {
          'ui:widget': ReverseToggleButton,
        },
        newCarIsWaiting: {
          'ui:widget': ReverseToggleButton,
        },
      },
    },
  };
};

export const step6UiSchema = {
  classNames: 'nested-form-container',
  fees: Object.values(Condition)
    .map(condition => ({
      [condition]: [
        ...Object.values(FinanceType),
        ...Object.values(ExtendedFinanceType),
      ]
        .map(financeType => ({
          [financeType]: Object.values(FeeCategory)
            .map(feeCategory => ({
              [feeCategory]: {
                classNames: 'nested-form-group',
                items: {
                  classNames: 'nested-form-group-no-indent',
                  type: {
                    classNames: 'col-md-3',
                  },
                  name: {
                    classNames: 'col-md-4',
                  },
                  value: {
                    classNames: 'col-md-2',
                  },
                  crmField: {
                    classNames: 'col-md-2',
                  },
                  apr: {
                    'ui:field': 'percent',
                    classNames: 'col-md-2',
                  },
                  includeInFeeStack: {
                    classNames: 'col-md-2',
                  },
                },
              },
            }))
            .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
        }))
        .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
    }))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {}),
};

export const getStep8UiSchema = formData => ({
  printingType: {
    classNames: 'col-md-12',
  },
  testDriveSpeed: {
    classNames: 'col-md-6',
  },
  testDriveDuration: {
    classNames: 'col-md-6',
  },
  supportEmail: {
    classNames: 'col-md-12',
  },
  defaultApr: {
    'ui:field': 'percent',
    classNames: 'col-md-12',
  },
  desking: {
    classNames: 'col-md-6 config-group',
    enabled: {
      'ui:widget': ToggleButton,
    },
    useTradeInAllowanceForTax: {
      enabled: {
        'ui:widget': ToggleButton,
      },
      [Condition.USED]: {
        classNames: 'pl-15',
      },
      [Condition.NEW]: {
        classNames: 'pl-15',
      },
      [Condition.CPO]: {
        classNames: 'pl-15',
      },
    },
    show10DollarMonthlyPriceRangeByDefault: {
      'ui:disabled': formData?.desking?.showPriceRangeWithoutAPROnSticker,
    },
    showPriceRangeWithoutAPROnSticker: {
      'ui:disabled': !formData?.desking?.show10DollarMonthlyPriceRangeByDefault,
      classNames: 'pl-15',
    },
    inceptionFeesTotalAtStart: {
      'ui:widget': !formData?.desking?.addInceptionFeesToTotalAtStart
        ? 'hidden'
        : undefined,
      classNames: 'pl-15',
      taxes: {
        enabled: {
          'ui:disabled': formData?.desking?.includeTaxInCapCost,
        },
      },
      fees: {
        feeStatus: {
          classNames: 'pl-15',
        },
      },
      customerToggle: {
        enabled: {
          classNames: 'checkbox-with-description',
        },
      },
    },
    includeTaxInCapCost: {
      'ui:disabled':
        formData?.desking?.inceptionFeesTotalAtStart?.taxes?.enabled,
    },

    taxInCapCost: {
      'ui:widget': !formData?.desking?.includeTaxInCapCost
        ? 'hidden'
        : undefined,
      classNames: 'pl-15',
      capCostReductionTax: {
        enabled: {
          'ui:disabled': !formData?.desking?.includeTaxInCapCost,
        },
      },
      paymentStreamTax: {
        enabled: {
          'ui:disabled': !formData?.desking?.includeTaxInCapCost,
        },
      },
      customerToggle: {
        enabled: {
          classNames: 'checkbox-with-description',
        },
      },
    },
    showSeparateLowInterestHighRebateOffers: {
      defaultOffer: {
        classNames: 'pl-15',
      },
    },
    useDeskingCRMPush: {
      'ui:disabled': !(
        formData?.crmType === CrmType.ELEAD ||
        formData?.crmType === CrmType.VINSOLUTIONS
      ),
    },
  },
  tabletFormsConfig: {
    classNames: 'col-md-5 config-group',
    customerInfo: {
      classNames: 'col-md-12',
      email: {
        classNames: 'col-md-6',
      },
      phone: {
        classNames: 'col-md-6',
      },
      zipCode: {
        classNames: 'col-md-6',
      },
      preferredContact: {
        classNames: 'col-md-12',
      },
      trackingCode: {
        classNames: 'col-md-12',
      },
      allowFormSkip: {
        classNames: 'col-md-12',
      },
      disableCustomerSignature: {
        classNames: 'col-md-12',
      },
      showInstoreOnlineLink: {
        classNames: 'col-md-12',
      },
      useAddCarWithoutVIN: {
        classNames: 'col-md-12',
      },
    },
    vehicleSelection: {
      classNames: 'col-md-12',
      monthlyPayment: {
        classNames: 'col-md-6',
      },
      daysUntilAgedUnit: {
        'ui:help':
          'Vehicles in stock above selected number of days will be dispayed as an Aged Unit on the Upstart iPad.',
        classNames: 'col-md-6',
      },
    },
    driverLicense: {
      classNames: 'col-md-12',
    },
    fiveLine: {
      classNames: 'col-md-12',
    },
    enableSalePendingPrompt: {
      classNames: 'col-md-6',
    },
  },
  wizard: {
    classNames: 'col-md-12',
    splashPage: {
      classNames: 'col-md-5 config-group',
      alternateHeaders: {
        classNames: 'col-md-12',
        'ui:widget': 'radio',
      },
    },
    hotLeads: {
      classNames: 'col-md-5 config-group hot-leads-config',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      emails: {
        classNames: 'col-md-12',
      },
    },
    priceValidation: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      kbbApiKey: {
        classNames: 'col-md-12',
      },
    },
    disclaimer: {
      classNames: 'col-md-5 config-group',
      payments: {
        classNames: 'col-md-12',
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 4,
        },
      },
    },
    paymentBuilder: {
      classNames: 'col-md-5 config-group',
      showPaymentSummary: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    leadForm: {
      classNames: 'col-md-5 config-group',
      piiFlow: {
        classNames: 'col-md-12',
        'ui:widget': 'radio',
      },
      zipCode: {
        classNames: 'col-md-12',
        enabled: {
          'ui:widget': ToggleButton,
        },
      },
      preferredContact: {
        classNames: 'col-md-12',
        enabled: {
          'ui:widget': ToggleButton,
        },
      },
    },
    ctaClassic: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    pickup: {
      classNames: 'col-md-5 config-group',
    },
    googleAnalytics: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    facebookPixel: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    updateEmails: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    delivery: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      radius: {
        'ui:widget': 'range',
      },
    },
    loadCarsFromPartnerStores: {
      classNames: 'col-md-5 config-group',
    },
    routeDealToWebsiteDealer: {
      classNames: 'col-md-5 config-group',
    },
    enableDocUpload: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': formData?.lending?.upstart?.enabled
          ? DisabledToggleButton()
          : ToggleButton,
      },
    },
  },
  email: {
    classNames: 'nested-form-group',
  },
  featureConfig: {
    classNames: 'col-md-12',
    inventoryImages: {
      classNames: 'col-md-6 config-group',
      primaryImageSource: {
        'ui:widget': 'radio',
        'ui:options': { label: false },
      },
    },
    showDealerBrandsFirst: {
      classNames: 'col-md-5 config-group show-dealer-brands-first',
      'ui:widget': 'radio',
    },
    inventoryDefaultSort: {
      classNames: 'col-md-5 config-group show-dealer-brands-first',
    },
    showOnLotInventoryFirst: {
      classNames: 'col-md-5 config-group show-dealer-brands-first',
      'ui:widget': 'radio',
    },
    inventoryConditionDisplay: {
      classNames: 'col-md-6 config-group',
      displayCheckboxes: {
        'ui:widget': 'radio',
      },
    },
    useCPO: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    ftpUpload: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      hostname: {
        classNames: 'col-md-12',
      },
      username: {
        classNames: 'col-md-12',
      },
      password: {
        classNames: 'col-md-12',
        'ui:widget': 'password',
      },
      path: {
        classNames: 'col-md-12',
      },
    },
    softCreditPull: {
      classNames: 'col-md-6 config-group',
      online: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      inStoreCreditApp: {
        classNames: 'col-md-6',
        'ui:widget': formData?.lending?.instore?.enabled
          ? ToggleButton
          : DisabledToggleButton(false),
        'ui:help': !formData?.lending?.instore?.enabled
          ? 'Lending must be enabled for this feature to be available.'
          : '',
      },
      inStore: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      digitalFinance: {
        classNames: 'col-md-6',
        'ui:widget': formData?.lending?.digitalFinance?.enabled
          ? ToggleButton
          : DisabledToggleButton(false),
        'ui:help': !formData?.lending?.digitalFinance?.enabled
          ? 'Digital Finance must be enabled for this feature to be available.'
          : '',
      },
      softUser: {
        classNames: 'col-md-6',
        'ui:autocomplete': 'off',
      },
      password: {
        classNames: 'col-md-6',
        'ui:widget': 'password',
      },
    },
    urbanScience: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      dealerCode: {
        classNames: 'col-md-12',
      },
    },
    newLendingTiersMatch: {
      classNames: 'col-md-5 config-group',
      'ui:options': {
        inline: true, // Ensures label and options are inline
      },
      purchase: {
        classNames: 'col-md-12 d-inline-flex align-items-center',
        'ui:widget': 'radio',
        'ui:options': {
          inline: true,
        },
      },
      lease: {
        classNames: 'col-md-12 d-inline-flex align-items-center',
        'ui:widget': 'radio',
        'ui:options': {
          inline: true,
        },
      },
    },
    thirdPartyLeads: {
      classNames: 'col-md-5 config-group',
      forwardToCustomer: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    deposits: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      newCars: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      usedCars: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      cpoCars: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      amount: {
        classNames: 'col-md-12',
      },
      stripePublicKey: {
        classNames: 'col-md-12',
        'ui:autocomplete': 'off',
      },
      stripePrivateKey: {
        classNames: 'col-md-12',
        'ui:autocomplete': 'off',
        'ui:widget': formData?.featureConfig?.deposits?.stripePrivateKey
          ? 'password'
          : 'text',
      },
      stripeWebhookEnabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    kiaReservation: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    crmDirectLogin: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    virtualShowroom: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      displayMultipleChatRequests: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    incentives: {
      classNames: 'col-md-5 config-group',
    },
    downPayment: {
      classNames: 'col-md-5 config-group',
      downPaymentOptions: {
        isPercentage: {
          'ui:widget': 'radio',
          'ui:options': { label: false },
        },
        'ui:options': { label: false },
      },
    },
    downPaymentTradeValueLimit: {
      classNames: 'col-md-5 config-group',
      [FinanceType.PURCHASE]: {
        enabled: {
          classNames: 'col-md-12',
          'ui:widget': ToggleButton,
        },
      },
      [FinanceType.LEASE]: {
        enabled: {
          classNames: 'col-md-12',
          'ui:widget': ToggleButton,
        },
      },
    },
    multiDealerManagement: {
      classNames: 'col-md-5 config-group',
    },
    kbbDefaultOption: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-6',
        'ui:disabled':
          formData?.featureConfig?.kbbDefaultOption?.enabled &&
          !formData?.featureConfig?.acuTrade?.enabled &&
          !formData?.featureConfig?.kbbIco?.enabled &&
          !formData?.featureConfig?.blackBook?.enabled &&
          (!formData?.featureConfig?.tradePending?.enabled ||
            (formData?.featureConfig?.tradePending?.enabled &&
              !formData?.featureConfig?.tradePending?.showOnline)),
        'ui:help':
          !formData?.featureConfig?.acuTrade?.enabled &&
          !formData?.featureConfig?.kbbIco?.enabled &&
          !formData?.featureConfig?.blackBook?.enabled &&
          (!formData?.featureConfig?.tradePending?.enabled ||
            (formData?.featureConfig?.tradePending?.enabled &&
              !formData?.featureConfig?.tradePending?.showOnline))
            ? 'Cannot be unselected unless another trade-in type is configured to show online.'
            : '',
      },
    },
    acuTrade: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      enabledInstore: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      dealerID: {
        classNames: 'col-md-12',
      },
      dealerKey: {
        classNames: 'col-md-12',
      },
      showKBBOption: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    kbbIco: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      enabledInstore: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      dealerId: {
        classNames: 'col-md-12',
      },
      groupId: {
        classNames: 'col-md-12',
      },
      groupIdRequired: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      showKBBOption: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    blackBook: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
    },
    tradePending: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      showInStore: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
      showOnline: {
        classNames: 'col-md-6',
        'ui:widget': ToggleButton,
      },
    },
    subaruGtp: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      showKBBOption: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    forwardInstabugReports: {
      classNames: 'col-md-5 config-group forward-instabug-reports-config',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
      emails: {
        classNames: 'col-md-12',
        'ui:options': {
          orderable: false,
        },
      },
    },
    formsConfig: {
      classNames: 'col-md-5 config-group',
      mandatoryEmployersAddress: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    displayPricesWithIncentives: {
      classNames: 'col-md-5 config-group',
      adjustOnline: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    eContracting: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    displaySavings: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    daysToFirstPayment: {
      classNames: 'col-md-5 config-group',
    },
    customFeedPrice: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    tradeEquity: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    showClassicOffers: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
    audiInTransit: {
      classNames: 'col-md-5 config-group',
      enabled: {
        classNames: 'col-md-12',
        'ui:widget': ToggleButton,
      },
    },
  },
});

export const getStep9UiSchema = localizationConfig => {
  localizationConfig = localizationConfig || {};
  let disabledOptions = [];
  if (get(localizationConfig, 'supportedLanguages')) {
    Object.values(Language).forEach(lang => {
      if (localizationConfig.supportedLanguages.indexOf(lang) === -1) {
        disabledOptions.push(lang);
      }
    });
  } else {
    disabledOptions = Object.values(Language);
  }
  return {
    localizationConfig: {
      supportedLanguages: {
        classNames: 'col-md-5',
        'ui:widget': 'checkboxes',
      },
      defaultLanguage: {
        classNames: 'col-md-5',
        'ui:widget': 'radio',
        'ui:enumDisabled': disabledOptions,
      },
      dateFormat: {
        classNames: 'col-md-5',
        'ui:widget': 'radio',
      },
      hourFormat: {
        classNames: 'col-md-5',
        'ui:widget': 'hidden', // temporarily hiding this feature until we want to fully support 24 hour format
      },
      fuelEfficiencyUnit: {
        classNames: 'col-md-5 clear-both',
        'ui:widget': 'radio',
      },
      currency: {
        classNames: 'col-md-5',
        'ui:widget': 'radio',
      },
      timezone: {
        classNames: 'col-md-3 no-padding-left clear-both',
      },
    },
  };
};

export const ownershipOptionsUISchema = {};

export const getStep10UiSchema = formData => {
  const storeRequestType = formData?.insurance?.instore?.requestType;
  return {
    insurance: {
      'ui:order': ['provider', 'insuranceEmail', 'online', 'instore'],
      online: {
        classNames: 'col-md-3 insurance-checkbox-container',
        enabled: { 'ui:widget': ToggleButton },
      },
      instore: {
        classNames: 'col-md-6 insurance-checkbox-container',
        enabled: { 'ui:widget': ToggleButton },
        requestType: {
          classNames: 'insurance-type-radio',
          'ui:options': { inline: true },
          'ui:widget': 'radio',
        },
        ...Object.values(InsuranceRequestType).reduce(
          (allTypes, type) => ({
            ...allTypes,
            [type]: {
              classNames: 'col-xs-6',
              'ui:disabled': storeRequestType !== type,
            },
          }),
          {},
        ),
      },
    },
  };
};

export const getStep11UiSchema = formData => {
  const instoreLendingEnabled = formData?.lending?.instore?.enabled;
  const instoreEnabled = formData?.wizard?.enableInstore;
  const enabled =
    instoreLendingEnabled ||
    formData?.lending?.online?.enabled ||
    formData?.lending?.digitalFinance?.enabled;
  const hasDigitalFinanceDMSType =
    formData?.dmsType === DMSType.CDK ||
    formData?.dmsType === DMSType.DT ||
    formData?.dmsType === DMSType.TEKION;
  const upstartStandaloneLendingEnabled =
    instoreEnabled && instoreLendingEnabled;
  return {
    lending: {
      'ui:order': [
        'online',
        'instore',
        'digitalFinance',
        'allowOptionalInvoiceOrBookValue',
        'westlake',
        'creditWidget',
        'upstart',
      ],
      online: {
        classNames: 'col-md-4',
        enabled: { 'ui:widget': ToggleButton },
      },
      instore: {
        'ui:order': [
          'enabled',
          'flowType',
          'aprMarkup',
          'enableAcceptOfferScreen',
          'manualFISubmission',
          'submitToSelectedLenders',
          'dealerId',
        ],
        dealerId: {
          'ui:widget': 'text',
        },
        classNames: 'col-md-4',
        enabled: { 'ui:widget': ToggleButton },
        flowType: {
          'ui:widget': 'radio',
          'ui:options': { inline: true },
        },
        enableAcceptOfferScreen: {
          'ui:widget': ToggleButton,
        },
        manualFISubmission: {
          'ui:widget': ToggleButton,
        },
        submitToSelectedLenders: {
          'ui:widget': ToggleButton,
        },
      },
      digitalFinance: {
        classNames: 'col-md-4',
        enabled: {
          'ui:widget': !hasDigitalFinanceDMSType
            ? DisabledToggleButton(false)
            : ToggleButton,
          'ui:help': !hasDigitalFinanceDMSType
            ? 'This feature is only available with CDK or Dealertrack DMS systems.'
            : '',
        },
        manualFISubmission: {
          'ui:widget': ToggleButton,
        },
        submitToSelectedLenders: {
          'ui:widget': ToggleButton,
        },
      },
      westlake: {
        classNames: 'col-md-5 config-group',
        enabled: { 'ui:widget': ToggleButton },
        'ui:widget': enabled ? undefined : 'hidden',
      },
      upstart: {
        classNames: 'col-md-5 config-group',
        enabled: { 'ui:widget': ToggleButton },
        'ui:widget': enabled ? undefined : 'hidden',
        upstartCreditFirst: {
          'ui:widget': ToggleButton,
        },
        standaloneLending: {
          enabled: {
            'ui:widget': !upstartStandaloneLendingEnabled
              ? DisabledToggleButton(false)
              : ToggleButton,
            'ui:help': !upstartStandaloneLendingEnabled
              ? 'Instore and Instore Lending must also be enabled'
              : '',
          },
        },
      },
      creditWidget: {
        classNames: 'col-md-5 config-group',
        online: { enabled: { 'ui:widget': ToggleButton } },
        instore: { enabled: { 'ui:widget': ToggleButton } },
        'ui:widget': enabled ? undefined : 'hidden',
      },
      allowOptionalInvoiceOrBookValue: {
        'ui:widget': ToggleButton,
      },
    },
  };
};

export const getStep12UiSchema = () => ({
  translationRules: {
    instore: {
      enabled: {
        'ui:widget': ToggleButton,
      },
      rulesId: {
        'ui:field': TranslationRulesWithPreviewLink,
      },
    },
  },
});
